import { ChangeEvent, SyntheticEvent, useEffect, useState } from "react";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { useMutation, useQueryClient } from "react-query";
import { defaultInvalidateQueryFilters, queryKeys } from "../../../api/query";
import { Panel } from "primereact/panel";
import { useNavigate, useParams } from "react-router-dom";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { useFields } from "../../../api/queries/enum/useFields";
import {
  CreateFieldPayload,
  FieldType,
  UpdateFieldPayload,
} from "../../../api/types/enum";
import { createFieldRequest } from "../../../api/queries/enum/createField";
import { updateFieldRequest } from "../../../api/queries/enum/updateField";
import { deleteFieldRequest } from "../../../api/queries/enum/deleteField";

export default function FieldForm() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { uuid } = useParams();

  const createField = useMutation({
    mutationFn: (payload: CreateFieldPayload) => createFieldRequest(payload),
  });

  const updateField = useMutation({
    mutationFn: (payload: UpdateFieldPayload) =>
      updateFieldRequest(uuid, payload),
  });

  const deleteField = useMutation({
    mutationFn: () => deleteFieldRequest(uuid),
  });

  const { data: fieldsResponse } = useFields();

  const [name, setName] = useState<string>("");
  const onNameChange = (event: ChangeEvent<HTMLInputElement>): void =>
    setName(event.target.value);

  const [parent, setParent] = useState<string>("");
  const onParentChange = (event: DropdownChangeEvent) => {
    setParent(event.value);

    const parent = fieldsResponse?.data.results.find(
      (item) => item.id === event.value,
    );

    if (parent) {
      setType(parent.type);
    }
  };

  const [type, setType] = useState<FieldType>(FieldType.Career);
  const onTypeChange = (event: DropdownChangeEvent) => setType(event.value);

  const handleSubmit = async (event: SyntheticEvent) => {
    event.preventDefault();

    if (uuid) {
      const payload: UpdateFieldPayload = {
        name,
        parent_id: parent || null,
        type,
      };

      await updateField.mutateAsync(payload);
    } else {
      const payload: CreateFieldPayload = {
        name,
        parent_id: parent || null,
        type,
      };

      await createField.mutateAsync(payload);
    }

    queryClient.invalidateQueries(
      queryKeys.fields(),
      defaultInvalidateQueryFilters,
    );

    navigate("/admin/fields");
  };

  const onDelete = async () => {
    await deleteField.mutateAsync();

    queryClient.invalidateQueries(
      queryKeys.fields(),
      defaultInvalidateQueryFilters,
    );

    navigate("/admin/fields");
  };

  useEffect(() => {
    if (!uuid || !fieldsResponse) return;

    const field = fieldsResponse.data.results.find((item) => item.id === uuid);

    if (field) {
      setName(field.name);
      setParent(field.parent?.id || "");
      setType(field.type);
    }
  }, [uuid, fieldsResponse]);

  return (
    <form onSubmit={handleSubmit} className="col-12">
      <Panel header="Field" toggleable={true} className="mb-3">
        <div className="p-fluid">
          <div className="field pt-3">
            <div className="p-float-label">
              <InputText value={name} onChange={onNameChange} />
              <label>Name *</label>
            </div>
          </div>
        </div>

        <div className="p-fluid">
          <div className="field pt-3">
            <div className="p-float-label">
              <Dropdown
                value={parent}
                onChange={onParentChange}
                options={fieldsResponse?.data.results || []}
                optionLabel="name"
                optionValue="id"
                showClear
              />
              <label>Parent</label>
            </div>
          </div>
        </div>

        <div className="p-fluid">
          <div className="field pt-3">
            <div className="p-float-label">
              <Dropdown
                value={type}
                onChange={onTypeChange}
                options={[
                  { label: "Career", value: FieldType.Career },
                  { label: "Education", value: FieldType.Education },
                ]}
                disabled={Boolean(parent)}
              />
              <label>Type *</label>
            </div>
          </div>
        </div>
      </Panel>

      <div className="flex gap-1 justify-content-end">
        {uuid && (
          <Button
            type="button"
            label="Delete"
            className="p-button-danger p-button-sm"
            onClick={onDelete}
          />
        )}

        <Button
          type="submit"
          label="Save"
          className="p-button-main p-button-sm"
          disabled={!name || !type}
        />
      </div>
    </form>
  );
}
